/* eslint-disable react/no-did-update-set-state */

import { Grid, Table, TableBody, TableCell, TablePagination, TableRow } from '@material-ui/core'
import { withStyles } from '@material-ui/styles'
import { compose, ensureFunction, except } from 'app/utils/fp'
import clsx from 'clsx'
import { filterSpecPropType } from 'core/components/cardTable/CardTableToolbar'
import Card from 'core/elements/card'
import { listTableActionPropType } from 'core/components/listTable/ListTableBatchActions'
import MoreMenu from 'core/components/MoreMenu'
import Progress from 'core/components/progress/Progress'
import PropTypes from 'prop-types'
import {
  any,
  assoc,
  assocPath,
  equals,
  includes,
  max,
  pipe,
  pluck,
  prop,
  propEq,
  propOr,
  uniq,
  update,
} from 'ramda'
import React, { PureComponent } from 'react'
import { emptyArr, emptyObj, isNilOrEmpty, pathStr } from 'utils/fp'
import { escapeRegex, memoize } from 'utils/misc'
import generateTestId from 'utils/test-helpers'
import NoContentMessage from '../NoContentMessage'
import ListTableHead from './ListTableHead'
import ListTableToolbar from './ListTableToolbar'
import Checkbox from 'core/elements/input/Checkbox'
import Radio from 'core/elements/input/Radio'

const styles = (theme) => ({
  root: {
    width: '100%',
    minHeight: 300,
  },
  compactTableHeight: {
    minHeight: 'auto',
  },
  tableWrapper: {
    overflowX: 'auto',
    '& .MuiTableRow-root': {
      '& .MuiTableCell-body': {
        '& > span': {
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
      },
    },
  },
  tableRow: {
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.components.table.hoverBackground,
    },
    '&.Mui-selected': {
      backgroundColor: theme.components.table.background,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.components.table.background,
    },
    borderBottom: `1px solid ${theme.components.table.border}`,
    '&:last-child td': {
      borderBottom: 0,
    },
  },
  cell: {
    padding: theme.spacing(2, 1),
    ...theme.typography.body2,
    color: theme?.components?.typography?.default,
    '& .dropdown-container': {
      // to counteract the styling on dropdowns in ValidatedForm
      width: 'unset',
    },
  },
  emptyList: {
    textAlign: 'left',
    margin: theme.spacing(1, 3),
  },
  tablePagination: {
    color: theme?.components?.typography?.default,
  },
})

const minSearchLength = 3

const determineCheckboxCond = (checkboxCond, row) => {
  if (!checkboxCond) {
    return true
  }
  return checkboxCond(row)
}

// Reject all columns that are not visible or excluded
export const pluckVisibleColumnIds = (columns) =>
  columns.filter((column) => column.display !== false && column.excluded !== true).map(prop('id'))

class ListTable extends PureComponent {
  constructor(props) {
    super(props)
    const {
      columns,
      selectedRows,
      visibleColumns,
      columnsOrder,
      rowsPerPage,
      orderBy,
      orderDirection,
    } = props
    this.state = {
      columns,
      visibleColumns: visibleColumns || pluckVisibleColumnIds(columns),
      columnsOrder: columnsOrder || pluck('id', columns),
      rowsPerPage: rowsPerPage,
      orderBy: orderBy || columns[0].id,
      orderDirection: orderDirection || 'asc',
      page: 0,
      selected: selectedRows || emptyArr,
      searchTerm: '',
      filterValues: {},
    }
  }

  // Reset selected when the list of items is updated
  // This will be the case upon updating the cluster dropdown,
  // refreshing the list, or if there is any other reason
  // that the list changes.
  // In the future we may want to look into triggering this
  // only for a subset of above reasons.
  componentDidUpdate(prevProps) {
    const { data } = prevProps
    const { selected } = this.state
    const selectedIds = this.pluckDataIds(selected)
    const existingSelectedRows = data.filter((row) => selectedIds.includes(this.getRowId(row)))

    if (!equals(existingSelectedRows, selected)) {
      this.setState({
        selected: existingSelectedRows,
      })
    }
  }

  getRowId = (row) => {
    const { uniqueIdentifier } = this.props
    return uniqueIdentifier instanceof Function
      ? uniqueIdentifier(row)
      : pathStr(uniqueIdentifier, row)
  }

  handleRequestSort = (event, property) => {
    const { onSortChange } = this.props
    const orderBy = property
    let orderDirection = 'desc'

    if (this.state.orderBy === property && this.state.orderDirection === 'desc') {
      orderDirection = 'asc'
    }
    this.setState({ orderDirection, orderBy }, () => {
      ensureFunction(onSortChange)(orderBy, orderDirection)
    })
  }

  sortData = (data) => {
    const { columns } = this.props
    const orderBy = this.state.orderBy || columns[0].id
    const sortWith = propOr(
      (prevValue, nextValue) => (nextValue < prevValue ? -1 : 1),
      'sortWith',
      columns.find(propEq('id', orderBy)),
    )
    const sortedRows = [...data].sort((a, b) => sortWith(b[orderBy], a[orderBy]))

    return this.state.orderDirection === 'asc' ? sortedRows : sortedRows.reverse()
  }

  areAllSelected = (data) => {
    const { selected } = this.state
    const { selectedRows = selected, checkboxCond } = this.props
    return data.every((row) => {
      if (checkboxCond) {
        // Ignore rows that do not meet condition criteria
        return includes(row, selectedRows) || !checkboxCond(row)
      }
      return includes(row, selectedRows)
    })
  }

  handleSelectAllClick = (event, checked) => {
    // Todo: exclude rows with no checkbox condition
    const { selected } = this.state
    const {
      paginate,
      onSelectedRowsChange,
      selectedRows = selected,
      onSelectAll,
      checkboxCond,
    } = this.props
    const filteredData = this.getFilteredRows()
    const paginatedData = paginate ? this.paginate(filteredData) : filteredData

    let newSelected
    if (checked) {
      // Add active paginated rows that are not already selected
      newSelected = uniq([...selectedRows, ...paginatedData])
    } else {
      // Remove active paginated rows from selected
      newSelected = selectedRows.filter((row) => !paginatedData.includes(row))
    }
    if (checkboxCond) {
      newSelected = newSelected.filter((item) => checkboxCond(item))
    }
    if (onSelectedRowsChange) {
      // Controlled mode
      onSelectedRowsChange(newSelected)
    } else {
      this.setState({
        selected: newSelected,
      })
    }

    onSelectAll && onSelectAll(newSelected, checked)
  }

  handleClick = memoize((row) => (event) => {
    // Todo: exclude rows with no checkbox condition
    const { selected } = this.state
    const isSelected = this.isSelected(row)
    const {
      multiSelection,
      onSelectedRowsChange,
      onSelect,
      selectedRows = selected,
      checkboxCond,
    } = this.props
    if (checkboxCond) {
      if (!checkboxCond(row)) {
        return
      }
    }
    if (!multiSelection) {
      if (onSelectedRowsChange) {
        // Controlled mode
        onSelectedRowsChange([row])
      } else {
        this.setState({
          selected: [row],
        })

        onSelect && onSelect(row)
      }
      return
    }
    const rowId = this.getRowId(row)
    const selectedIndex = selectedRows.findIndex(
      (selectedRow) => this.getRowId(selectedRow) === rowId,
    )
    const newSelected = []

    if (selectedIndex === -1) {
      // not found
      newSelected.push(...selectedRows, row)
    } else if (selectedIndex === 0) {
      // first
      newSelected.push(...selectedRows.slice(1))
    } else if (selectedIndex === selectedRows.length - 1) {
      // last
      newSelected.push(...selectedRows.slice(0, -1))
    } else if (selectedIndex > 0) {
      // somewhere inbetween
      newSelected.push(
        ...selectedRows.slice(0, selectedIndex),
        ...selectedRows.slice(selectedIndex + 1),
      )
    }

    if (onSelectedRowsChange) {
      // Controlled mode
      onSelectedRowsChange(newSelected)
    } else {
      this.setState({
        selected: newSelected,
      })

      onSelect && onSelect(row, isSelected)
    }
  })

  handleChangePage = (event, page) => this.setState({ page })

  handleChangeRowsPerPage = (e) => {
    const { value: rowsPerPage } = e.target
    this.setState({ rowsPerPage }, () =>
      ensureFunction(this.props.onRowsPerPageChange)(rowsPerPage),
    )
  }

  handleAdd = () => {
    this.props.onAdd()
  }

  handleDelete = async () => {
    const { selected } = this.state
    const { onDelete, data, selectedRows = selected, onSelectedRowsChange } = this.props
    await onDelete(selectedRows)
    this.setState(
      {
        page: this.getCurrentPage(data.length),
      },
      () => {
        if (onSelectedRowsChange) {
          // Controlled mode
          onSelectedRowsChange(emptyArr)
        } else {
          this.setState({
            selected: emptyArr,
          })
        }
      },
    )
  }

  handleEdit = () => {
    ensureFunction(this.props.onEdit)(this.state.selected)
  }

  handleSearch = (value) => {
    if (this.props.searchTargets) {
      this.setState({
        searchTerm: value,
      })
    }
  }

  handleColumnToggle = (columnId) => {
    const { columns } = this.props
    const existingColumnIds = columns.map((column) => column.id)
    if (this.props.canEditColumns) {
      this.setState(
        ({ visibleColumns, columnsOrder }) => ({
          visibleColumns: (visibleColumns.includes(columnId)
            ? except(columnId, visibleColumns)
            : [...visibleColumns, columnId]
          ).filter((column) => existingColumnIds.includes(column)),
          columnsOrder: (columnsOrder.includes(columnId)
            ? columnsOrder
            : [...columnsOrder, columnId]
          ).filter((column) => existingColumnIds.includes(column)),
        }),
        () => {
          ensureFunction(this.props.onColumnsChange)(
            this.state.visibleColumns,
            this.state.columnsOrder,
          )
        },
      )
    }
  }

  handleColumnsSwitch = (srcColumnId, destColumnId) => {
    const { columnsOrder } = this.state
    const srcColumnIdx = columnsOrder.indexOf(srcColumnId)
    const tarColumnIdx = columnsOrder.indexOf(destColumnId)
    this.setState(
      ({ columnsOrder }) => ({
        columnsOrder: pipe(
          update(srcColumnIdx, destColumnId),
          update(tarColumnIdx, srcColumnId),
        )(columnsOrder),
      }),
      () =>
        ensureFunction(this.props.onColumnsChange)(
          this.state.visibleColumns,
          this.state.columnsOrder,
        ),
    )
  }

  handleFilterUpdate = (columnId, selectedValue) => {
    this.setState(assocPath(['filterValues', columnId], selectedValue))
  }

  handleFiltersReset = () => {
    this.setState(assoc('filterValues', {}))
  }

  getFilterFunction = (type) => {
    switch (type) {
      case 'select':
        return equals
      case 'multiselect':
        return (filterValues, value) => any(equals(value))(filterValues)
      case 'checkbox':
        return equals
      default:
        return equals
    }
  }

  applyFilters = (data) => {
    const { filters } = this.props
    const { filterValues } = this.state
    const filterParams = Object.entries(filterValues).map(([columnId, filterValue]) => ({
      columnId,
      filterValue,
      filter: filters.find(propEq('columnId', columnId)),
    }))

    return filterParams.reduce((filteredData, { columnId, filterValue, filter }) => {
      if (filter.onChange) {
        // If a custom handler is provided, don't filter the data locally
        return filteredData
      }
      const filterWith = filter.filterWith || this.getFilterFunction(filter.type)

      return filteredData.filter((row) => {
        return filterWith(filterValue, row[columnId])
      })
    }, data)
  }

  filterBySearch = (data, targets) => {
    const { searchTerm } = this.state
    const escapedTerm = escapeRegex(searchTerm)
    return data.filter((ele) =>
      targets.some((target) => pathStr(target, ele).match(new RegExp(escapedTerm, 'i')) !== null),
    )
  }

  pluckDataIds = (rows) => rows.map(this.getRowId)

  isSelected = (row) => {
    const { selected } = this.state
    const { selectedRows = selected } = this.props
    const selectedIds = this.pluckDataIds(selectedRows)
    const rowId = this.getRowId(row)
    return selectedIds.includes(rowId)
  }

  paginate = (data) => {
    const { rowsPerPage } = this.state

    const startIdx = this.getCurrentPage(data.length) * rowsPerPage
    const endIdx = startIdx + rowsPerPage
    return data.slice(startIdx, endIdx)
  }

  getFilteredRows = () => {
    const { searchTargets, data, filters, onSortChange } = this.props
    const { searchTerm } = this.state

    const sortedData = onSortChange ? data : this.sortData(data)
    const searchData =
      !searchTerm || searchTerm.length < minSearchLength
        ? sortedData
        : this.filterBySearch(sortedData, searchTargets)
    return filters ? this.applyFilters(searchData) : searchData
  }

  renderCell = memoize((columnDef, contents, row, isSelected, cellClass) => {
    const { cellProps = emptyObj, id, render, Component: CellComponent } = columnDef
    let renderedCell = contents

    if (typeof contents === 'boolean') {
      renderedCell = String(renderedCell)
    }

    // Allow for customized rendering in the columnDef.  The render function might need
    // to know more about the entire object (row) being rendered and in some cases the
    if (render) {
      renderedCell = render(contents, row)
    } else if (CellComponent) {
      renderedCell = <CellComponent key={id} row={row} data={contents} isSelected={isSelected} />
    }

    return (
      <TableCell data-testid={generateTestId(id)} className={cellClass} key={id} {...cellProps}>
        {renderedCell}
      </TableCell>
    )
  })

  renderRowActions = (row) => {
    const { rowActions, onRefresh, onActionComplete = onRefresh } = this.props
    if (isNilOrEmpty(rowActions)) {
      return null
    }
    return (
      <TableCell>
        <MoreMenu items={rowActions} onComplete={onActionComplete} data={row} />
      </TableCell>
    )
  }

  getSortedVisibleColumns = () => {
    const { columns } = this.props
    const { columnsOrder, visibleColumns } = this.state
    return columnsOrder
      .map((columnId) => columns.find((column) => column.id === columnId))
      .filter((column) => column && column.id && visibleColumns.includes(column.id))
  }

  renderRow = (row) => {
    const { multiSelection, showCheckboxes, checkboxCond, classes } = this.props
    let isSelected = this.isSelected(row)

    const checkboxProps = showCheckboxes
      ? {
          onClick: this.handleClick(row),
          role: 'checkbox',
          tabIndex: -1,
          selected: isSelected,
        }
      : {}

    const uid = this.getRowId(row)

    return (
      <TableRow
        data-testid={generateTestId(this.props.id)}
        hover
        key={uid}
        {...checkboxProps}
        className={classes.tableRow}
      >
        {showCheckboxes && (
          <TableCell
            data-testid={generateTestId('check', 'box', 'cluster', 'selection')}
            padding="checkbox"
          >
            {determineCheckboxCond(checkboxCond, row) && (
              <>
                {multiSelection ? (
                  <Checkbox checked={isSelected} />
                ) : (
                  <Radio checked={isSelected} />
                )}
              </>
            )}
          </TableCell>
        )}
        {this.getSortedVisibleColumns().map((columnDef) =>
          this.renderCell(columnDef, pathStr(columnDef.id, row), row, isSelected, classes.cell),
        )}
        {this.renderRowActions(row)}
      </TableRow>
    )
  }

  getCurrentPage = (count) => {
    const { page, rowsPerPage } = this.state
    const lastPage = max(Math.ceil(count / rowsPerPage) - 1, 0)
    return page > lastPage ? lastPage : page
  }

  renderPaginationControls = (count) => {
    const { rowsPerPage } = this.state
    const { classes } = this.props
    return (
      <TablePagination
        // labelRowsPerPage=""
        // labelDisplayedRows={() => ''}
        className={classes.tablePagination}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={this.getCurrentPage(count)}
        backIconButtonProps={{ 'arial-label': 'Previous Page' }}
        nextIconButtonProps={{ 'arial-label': 'Next Page' }}
        onPageChange={this.handleChangePage}
        onRowsPerPageChange={this.handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
    )
  }

  renderEmptyList = () => {
    if (this.props.loading) {
      return null
    }
    return <NoContentMessage message={this.props.emptyText} />
  }

  render() {
    const {
      orderDirection,
      orderBy,
      searchTerm,
      selected,
      visibleColumns,
      filterValues,
      rowsPerPage,
    } = this.state
    const {
      className,
      batchActions,
      classes,
      columns,
      data,
      paginate,
      showCheckboxes,
      canDragColumns,
      filters,
      onReload,
      onRefresh,
      loading,
      loadingMessage,
      onAdd,
      onDelete,
      deleteCond,
      deleteDisabledInfo,
      onEdit,
      editCond,
      editDisabledInfo,
      selectedRows = selected,
      size,
      compactTable,
      blankFirstColumn,
      extraToolbarContent,
      multiSelection,
      headless,
      hideDelete,
      alternativeTableContent,
      listTableParams,
      showPagination = !compactTable,
      selectColumns = true,
      dataTestId,
      wrapToolbar,
    } = this.props

    if (!data) {
      return null
    }

    const filteredData = this.getFilteredRows()
    const paginatedData = paginate ? this.paginate(filteredData) : filteredData
    const selectedAll = this.areAllSelected(paginatedData)
    // Always show pagination control bar to make sure the height doesn't change frequently.
    // const shouldShowPagination = paginate && sortedData.length > this.state.rowsPerPage

    const tableContent =
      paginatedData && paginatedData.length ? (
        <Table className={classes.table} size={size}>
          {!headless && (
            <ListTableHead
              canDragColumns={canDragColumns}
              columns={this.getSortedVisibleColumns()}
              onColumnsSwitch={this.handleColumnsSwitch}
              numSelected={selectedRows.length}
              order={orderDirection}
              orderBy={orderBy}
              onSelectAllClick={this.handleSelectAllClick}
              onRequestSort={this.handleRequestSort}
              checked={selectedAll}
              rowCount={filteredData.length}
              showCheckboxes={showCheckboxes}
              blankFirstColumn={blankFirstColumn}
              multiSelection={multiSelection}
            />
          )}
          <TableBody>{paginatedData.map(this.renderRow)}</TableBody>
        </Table>
      ) : (
        this.renderEmptyList()
      )

    return (
      <Progress
        loading={loading}
        message={loadingMessage}
        overlay
        renderContentOnMount
        inline={compactTable}
      >
        <div
          className={clsx(
            'ListTable-root',
            className,
            classes.root,
            compactTable && classes.compactTableHeight,
          )}
        >
          <Card
            title={
              !compactTable && (
                <ListTableToolbar
                  extraToolbarContent={extraToolbarContent}
                  selected={selectedRows}
                  onAdd={onAdd && this.handleAdd}
                  onDelete={onDelete && this.handleDelete}
                  deleteCond={deleteCond}
                  deleteDisabledInfo={deleteDisabledInfo}
                  onEdit={onEdit && this.handleEdit}
                  editCond={editCond}
                  editDisabledInfo={editDisabledInfo}
                  onSearchChange={this.handleSearch}
                  searchTerm={searchTerm}
                  columns={columns}
                  visibleColumns={visibleColumns}
                  onColumnToggle={selectColumns && this.handleColumnToggle}
                  filters={filters}
                  filterValues={filterValues}
                  onFilterUpdate={this.handleFilterUpdate}
                  onFiltersReset={this.handleFiltersReset}
                  onReload={onReload}
                  onRefresh={onRefresh}
                  batchActions={batchActions}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={this.handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  hideDelete={hideDelete}
                  listTableParams={listTableParams}
                  wrapToolbar={wrapToolbar}
                />
              )
            }
            footer={
              !alternativeTableContent &&
              showPagination &&
              this.renderPaginationControls(filteredData.length)
            }
          >
            {alternativeTableContent || <div className={classes.tableWrapper}>{tableContent}</div>}
          </Card>
        </div>
      </Progress>
    )
  }
}

ListTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      render: PropTypes.func,
      sortWith: PropTypes.func,
      disableSorting: PropTypes.bool,
      /* Not displayed columns will only appear in the columns selector */
      display: PropTypes.bool,
      /* Excluded columns will neither appear in the grid nor in the columns selector */
      excluded: PropTypes.bool,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  options: PropTypes.object,
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  deleteCond: PropTypes.func,
  deleteDisabledInfo: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  hideDelete: PropTypes.bool,
  onEdit: PropTypes.func,
  editCond: PropTypes.func,
  editDisabledInfo: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onReload: PropTypes.func,
  onRefresh: PropTypes.func,
  onActionComplete: PropTypes.func,
  paginate: PropTypes.bool,
  showPagination: PropTypes.bool,
  orderBy: PropTypes.string,
  orderDirection: PropTypes.oneOf(['asc', 'desc']),
  onSortChange: PropTypes.func,

  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  rowsPerPage: PropTypes.number,
  emptyText: PropTypes.string,

  /**
   * List of filters
   */
  filters: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(filterSpecPropType)]),

  /*
   Some objects have a unique identifier other than 'id'
   For example sshKeys have unique identifier of 'name' and the APIs
   rely on using the name as part of the URI. Specify the unique identifier
   in props if it is different from 'id'

   For more complicated scenarios, you can pass a funciton that receives the row data and returns the uid.
   It has the following type signature:
     uniqueIdentifier :: RowData -> String
   */
  uniqueIdentifier: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * List of batch actions that can be performed
   * on the selected items.
   */
  batchActions: PropTypes.arrayOf(listTableActionPropType),

  /**
   * List of actions that can be performed on a single row.
   */
  rowActions: PropTypes.arrayOf(listTableActionPropType),

  onRowsPerPageChange: PropTypes.func,
  onColumnsChange: PropTypes.func,

  showCheckboxes: PropTypes.bool,
  searchTargets: PropTypes.arrayOf(PropTypes.string),

  canEditColumns: PropTypes.bool,
  canDragColumns: PropTypes.bool,

  loading: PropTypes.bool,
  loadingMessage: PropTypes.string,

  /**
   * Wether or not to allow selecting multiple rows (checkboxes) or just one at a time (radio boxes)
   */
  multiSelection: PropTypes.bool,

  selectedRows: PropTypes.array,
  onSelectedRowsChange: PropTypes.func,
  onSelect: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium']),
  compactTable: PropTypes.bool,
  headless: PropTypes.bool,

  extraToolbarContent: PropTypes.node,
  wrapToolbar: PropTypes.bool,
}

ListTable.defaultProps = {
  paginate: true,
  showPagination: true,
  showCheckboxes: true,
  multiSelection: true,
  uniqueIdentifier: 'id',
  canEditColumns: true,
  canDragColumns: true,
  rowsPerPage: 10,
  emptyText: 'No data found',
  loading: false,
  size: 'medium',
  compactTable: false,
}

export default compose(withStyles(styles))(ListTable)
